// 12-level hand-authored Spanish course
// Each lesson builds on the previous. Words used in `practice` are drillable after the lesson.

export const LESSONS = [
  {
    id: 'greetings',
    level: 1,
    title: 'Greetings & Politeness',
    emoji: '👋',
    duration: '5 min',
    subtitle: 'Open every conversation like a local.',
    body: [
      { kind: 'p', text: 'Spanish greetings are warmer and a bit more elaborate than in English. People often shake hands or do a light cheek kiss, even with strangers. Learning these phrases is the single highest-impact thing you can do — they buy you instant goodwill anywhere from Madrid to Mexico City.' },
      { kind: 'h', text: 'The essential five' },
      { kind: 'examples', items: [
        { es: 'Hola', en: 'Hello', note: 'Universal — works any time of day.' },
        { es: 'Buenos días', en: 'Good morning', note: 'Until ~12:00 noon.' },
        { es: 'Buenas tardes', en: 'Good afternoon / evening', note: 'After noon until dark.' },
        { es: 'Buenas noches', en: 'Good night', note: 'After dark — used to greet AND to say goodnight.' },
        { es: 'Adiós', en: 'Goodbye' },
      ] },
      { kind: 'h', text: 'Politeness opens doors' },
      { kind: 'examples', items: [
        { es: 'Por favor', en: 'Please' },
        { es: 'Gracias', en: 'Thank you' },
        { es: 'De nada', en: "You're welcome" },
        { es: 'Disculpe', en: 'Excuse me (formal — getting attention)' },
        { es: 'Perdón', en: 'Sorry / pardon' },
        { es: 'Lo siento', en: "I'm sorry (heartfelt apology)" },
      ] },
      { kind: 'tip', text: 'Use "disculpe" to flag a waiter or stop a stranger to ask directions. Use "perdón" if you bump into someone. Use "lo siento" for serious apologies — never the other way around.' },
      { kind: 'h', text: 'Meeting someone new' },
      { kind: 'examples', items: [
        { es: '¿Cómo estás?', en: 'How are you?' },
        { es: 'Mucho gusto', en: 'Nice to meet you' },
        { es: 'Encantado / Encantada', en: 'Pleased to meet you', note: '-o if you are male, -a if female.' },
        { es: 'Hasta luego', en: 'See you later' },
      ] },
      { kind: 'tip', text: 'Match the energy of the greeting you receive. If someone says ¡Hola, buenos días! cheerfully, return the same — it sets a friendly tone.' },
    ],
    practice: ['hola', 'adiós', 'gracias', 'por favor', 'de nada', 'disculpe', 'perdón', 'lo siento', 'mucho gusto', 'buenos días', 'buenas noches', 'buenas tardes', 'hasta luego', 'encantado'],
  },
  {
    id: 'articles-gender',
    level: 2,
    title: 'Articles & Gender',
    emoji: '🔤',
    duration: '7 min',
    subtitle: 'Every Spanish noun is masculine or feminine. Yes, every one.',
    body: [
      { kind: 'p', text: 'Spanish nouns have grammatical gender. This is not a description of the object itself — el problema (problem) is masculine, la mesa (table) is feminine. The good news: there are simple patterns that work ~80% of the time.' },
      { kind: 'h', text: 'The two definite articles (the)' },
      { kind: 'examples', items: [
        { es: 'el hombre', en: 'the man (masculine)' },
        { es: 'la mujer', en: 'the woman (feminine)' },
        { es: 'el libro', en: 'the book (masc.)' },
        { es: 'la mesa', en: 'the table (fem.)' },
      ] },
      { kind: 'h', text: 'The two indefinite articles (a/an)' },
      { kind: 'examples', items: [
        { es: 'un taxi', en: 'a taxi (m)' },
        { es: 'una reserva', en: 'a reservation (f)' },
      ] },
      { kind: 'h', text: 'The 80% rule' },
      { kind: 'rules', items: [
        { label: 'Ends in -o', text: 'Usually masculine → el libro, el queso, el hijo' },
        { label: 'Ends in -a', text: 'Usually feminine → la casa, la mesa, la fruta' },
        { label: 'Ends in -ción / -dad / -tad', text: 'Always feminine → la nación, la ciudad, la libertad' },
        { label: 'Ends in -ma (Greek-origin)', text: 'Often masculine → el problema, el sistema, el idioma' },
      ] },
      { kind: 'tip', text: 'Always learn a noun WITH its article. Drill "la mesa" together as one chunk, never just "mesa". This builds gender into your muscle memory and avoids years of guessing.' },
      { kind: 'h', text: 'Common gotchas' },
      { kind: 'examples', items: [
        { es: 'el día', en: 'the day', note: 'Masculine despite ending in -a.' },
        { es: 'la mano', en: 'the hand', note: 'Feminine despite ending in -o.' },
        { es: 'el agua', en: 'the water', note: 'Feminine, but uses "el" because the next word starts with stressed "a".' },
      ] },
    ],
    practice: ['el', 'la', 'un', 'una', 'hombre', 'mujer', 'casa', 'libro', 'mesa', 'agua', 'mano'],
  },
  {
    id: 'pronouns',
    level: 3,
    title: 'Subject Pronouns',
    emoji: '🙋',
    duration: '4 min',
    subtitle: 'I, you, he, she — and why Spanish often hides them.',
    body: [
      { kind: 'p', text: 'Subject pronouns tell you WHO is doing the action. Spanish has more variety than English, and a unique party trick: most of the time, you can drop them entirely.' },
      { kind: 'examples', items: [
        { es: 'yo', en: 'I' },
        { es: 'tú', en: 'you (informal singular)' },
        { es: 'usted', en: 'you (formal singular)', note: 'Used with strangers, elders, bosses.' },
        { es: 'él', en: 'he' },
        { es: 'ella', en: 'she' },
        { es: 'nosotros / nosotras', en: 'we (m / f)' },
        { es: 'vosotros / vosotras', en: 'you all (Spain only, informal)' },
        { es: 'ustedes', en: 'you all (Latin America, or formal in Spain)' },
        { es: 'ellos / ellas', en: 'they (m / f)' },
      ] },
      { kind: 'tip', text: 'Spanish verbs already encode the subject in their endings. So "Hablo español" already means "I speak Spanish" — saying "Yo hablo español" adds emphasis ("I, on the other hand…") but is usually unnecessary.' },
      { kind: 'h', text: 'Tú vs Usted — when to switch' },
      { kind: 'rules', items: [
        { label: 'Use tú with', text: 'friends, family, peers, kids, anyone who invites you to be informal.' },
        { label: 'Use usted with', text: 'strangers (especially older), service workers, in business, until invited otherwise.' },
        { label: 'In Latin America', text: 'Many regions default to usted to be safe. In Spain, tú is the safer default among adults.' },
      ] },
    ],
    practice: ['yo', 'tú', 'él', 'ella'],
  },
  {
    id: 'present-ar',
    level: 4,
    title: 'Present Tense — AR Verbs',
    emoji: '📚',
    duration: '8 min',
    subtitle: '50% of Spanish verbs end in -ar. Learn this pattern once, unlock thousands.',
    body: [
      { kind: 'p', text: 'A "regular -ar verb" works like a perfectly tuned engine. Take the infinitive, drop the -ar, add the right ending. Done.' },
      { kind: 'h', text: 'Pattern: hablar (to speak)' },
      { kind: 'examples', items: [
        { es: 'yo hablo', en: 'I speak' },
        { es: 'tú hablas', en: 'you speak' },
        { es: 'él / ella habla', en: 'he / she speaks' },
        { es: 'nosotros hablamos', en: 'we speak' },
        { es: 'vosotros habláis', en: "you all speak (Spain)" },
        { es: 'ellos / ellas hablan', en: 'they speak' },
      ] },
      { kind: 'h', text: 'The endings' },
      { kind: 'rules', items: [
        { label: 'Step 1', text: 'Take the infinitive: HABLAR' },
        { label: 'Step 2', text: 'Drop the -ar → HABL-' },
        { label: 'Step 3', text: 'Add: -o, -as, -a, -amos, -áis, -an' },
      ] },
      { kind: 'h', text: 'Other -ar verbs that follow the pattern' },
      { kind: 'examples', items: [
        { es: 'trabajar', en: 'to work', note: '→ trabajo, trabajas, trabaja…' },
        { es: 'caminar', en: 'to walk', note: '→ camino, caminas, camina…' },
        { es: 'comprar', en: 'to buy', note: '→ compro, compras, compra…' },
        { es: 'necesitar', en: 'to need', note: '→ necesito, necesitas, necesita…' },
        { es: 'esperar', en: 'to wait / hope' },
        { es: 'ayudar', en: 'to help' },
        { es: 'mirar', en: 'to watch / look' },
        { es: 'escuchar', en: 'to listen' },
      ] },
      { kind: 'tip', text: 'Once a new -ar verb pops up, you already know all six conjugations. This is the pattern that does the most heavy lifting in Spanish — over-practice it until it feels boring.' },
    ],
    practice: ['hablar', 'hablo', 'hablas', 'habla', 'trabajar', 'caminar', 'comprar', 'necesitar', 'ayudar', 'esperar', 'mirar', 'escuchar'],
  },
  {
    id: 'present-er-ir',
    level: 5,
    title: 'Present Tense — ER & IR Verbs',
    emoji: '🍽️',
    duration: '7 min',
    subtitle: '-ER and -IR verbs share almost all their endings. Two patterns down, language transformed.',
    body: [
      { kind: 'p', text: 'The other 50% of regular Spanish verbs end in -er or -ir. Their endings are nearly identical, with one tiny twist for "we".' },
      { kind: 'h', text: 'Pattern: comer (to eat)' },
      { kind: 'examples', items: [
        { es: 'yo como', en: 'I eat' },
        { es: 'tú comes', en: 'you eat' },
        { es: 'él / ella come', en: 'he / she eats' },
        { es: 'nosotros comemos', en: 'we eat' },
        { es: 'vosotros coméis', en: 'you all eat' },
        { es: 'ellos comen', en: 'they eat' },
      ] },
      { kind: 'h', text: 'Pattern: vivir (to live)' },
      { kind: 'examples', items: [
        { es: 'yo vivo', en: 'I live' },
        { es: 'tú vives', en: 'you live' },
        { es: 'él / ella vive', en: 'he / she lives' },
        { es: 'nosotros vivimos', en: 'we live', note: 'Note the -imos (not -emos)' },
        { es: 'vosotros vivís', en: 'you all live', note: 'Note the -ís (not -éis)' },
        { es: 'ellos viven', en: 'they live' },
      ] },
      { kind: 'tip', text: 'The only difference between -er and -ir verbs in the present tense is the "nosotros" and "vosotros" forms. Everything else is identical. So if you know -er, you know 90% of -ir.' },
      { kind: 'h', text: 'High-frequency -er / -ir verbs' },
      { kind: 'examples', items: [
        { es: 'beber', en: 'to drink' },
        { es: 'leer', en: 'to read' },
        { es: 'aprender', en: 'to learn' },
        { es: 'vivir', en: 'to live' },
        { es: 'escribir', en: 'to write' },
        { es: 'salir', en: 'to leave / go out' },
      ] },
    ],
    practice: ['comer', 'como', 'comes', 'come', 'beber', 'bebo', 'bebes', 'bebe', 'vivir', 'leer', 'escribir', 'salir'],
  },
  {
    id: 'ser-estar',
    level: 6,
    title: 'Ser vs Estar — The Two "To Be"s',
    emoji: '⚖️',
    duration: '8 min',
    subtitle: 'Spanish splits "to be" into two verbs. Get this and you sound fluent fast.',
    body: [
      { kind: 'p', text: 'English uses one verb — "to be" — for everything. Spanish uses TWO: ser and estar. Choosing the wrong one is the #1 telltale sign of a beginner. Choosing the right one is one of the fastest ways to sound legit.' },
      { kind: 'h', text: 'Use SER for permanent / defining traits' },
      { kind: 'rules', items: [
        { label: 'D — Description', text: 'Soy alto. (I am tall.)' },
        { label: 'O — Occupation', text: 'Es médico. (He is a doctor.)' },
        { label: 'C — Characteristic', text: 'Eres amable. (You are kind.)' },
        { label: 'T — Time', text: 'Son las tres. (It is three.)' },
        { label: 'O — Origin', text: 'Soy de México. (I am from Mexico.)' },
        { label: 'R — Relationship', text: 'Es mi hermana. (She is my sister.)' },
      ] },
      { kind: 'h', text: 'Use ESTAR for location, mood, and temporary states' },
      { kind: 'rules', items: [
        { label: 'P — Position', text: 'Estoy aquí. (I am here.)' },
        { label: 'L — Location', text: 'El hotel está cerca. (The hotel is nearby.)' },
        { label: 'A — Action / progress', text: 'Estoy comiendo. (I am eating.)' },
        { label: 'C — Condition', text: 'Estás cansado. (You are tired.)' },
        { label: 'E — Emotion', text: 'Está contenta. (She is happy.)' },
      ] },
      { kind: 'tip', text: 'Mnemonic: SER = Doctor, ESTAR = Place. If it goes on a resume or birth certificate, use SER. If it could change tomorrow, use ESTAR.' },
      { kind: 'h', text: 'The same word, two meanings' },
      { kind: 'examples', items: [
        { es: 'Es aburrido', en: 'He is boring (personality)' },
        { es: 'Está aburrido', en: 'He is bored (right now)' },
        { es: 'Es bueno', en: 'It is good (in nature)' },
        { es: 'Está bueno', en: 'It tastes good (right now)' },
      ] },
    ],
    practice: ['ser', 'estar', 'soy', 'eres', 'es', 'estoy', 'estás', 'está', 'cansado', 'contento'],
  },
  {
    id: 'questions',
    level: 7,
    title: 'Asking Questions',
    emoji: '❓',
    duration: '6 min',
    subtitle: 'Master the seven question words and you can navigate any conversation.',
    body: [
      { kind: 'p', text: 'In Spanish, every question gets an upside-down ¿ at the start. This is not optional — it tells the reader from the very first character that they\'re reading a question.' },
      { kind: 'h', text: 'The seven essentials' },
      { kind: 'examples', items: [
        { es: '¿Qué?', en: 'What?' },
        { es: '¿Quién?', en: 'Who?' },
        { es: '¿Cuándo?', en: 'When?' },
        { es: '¿Dónde?', en: 'Where?' },
        { es: '¿Cómo?', en: 'How?' },
        { es: '¿Cuánto?', en: 'How much?' },
        { es: '¿Por qué?', en: 'Why?', note: '"Porque" (no space, no accent) means "because".' },
      ] },
      { kind: 'h', text: 'Putting them to work' },
      { kind: 'examples', items: [
        { es: '¿Cómo te llamas?', en: 'What is your name?', note: 'Literally: "How do you call yourself?"' },
        { es: '¿Dónde está el baño?', en: 'Where is the bathroom?' },
        { es: '¿Cuánto cuesta?', en: 'How much does it cost?' },
        { es: '¿Qué hora es?', en: 'What time is it?' },
        { es: '¿Por qué no?', en: 'Why not?' },
      ] },
      { kind: 'tip', text: 'When you don\'t catch what someone said, just say "¿Cómo?" — it\'s the polite Spanish "what?". Saying "¿Qué?" alone can come off blunt.' },
    ],
    practice: ['qué', 'quién', 'cuándo', 'dónde', 'cómo', 'cuánto', 'por qué'],
  },
  {
    id: 'numbers-time',
    level: 8,
    title: 'Numbers, Days & Time',
    emoji: '🕐',
    duration: '9 min',
    subtitle: 'Tell the time, count anything, plan a week.',
    body: [
      { kind: 'h', text: 'Numbers 0–10' },
      { kind: 'examples', items: [
        { es: 'cero, uno, dos, tres, cuatro, cinco, seis, siete, ocho, nueve, diez', en: '0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10' },
      ] },
      { kind: 'h', text: '11–20' },
      { kind: 'examples', items: [
        { es: 'once, doce, trece, catorce, quince', en: '11, 12, 13, 14, 15', note: 'These are unique — memorize them.' },
        { es: 'dieciséis, diecisiete, dieciocho, diecinueve, veinte', en: '16, 17, 18, 19, 20', note: 'Pattern: diez + y + digit, fused.' },
      ] },
      { kind: 'h', text: '21–99' },
      { kind: 'p', text: 'After 30, the pattern is simple: TENS + y + UNIT. Treinta y uno (31), cuarenta y dos (42), noventa y nueve (99).' },
      { kind: 'h', text: 'Telling the time' },
      { kind: 'examples', items: [
        { es: '¿Qué hora es?', en: 'What time is it?' },
        { es: 'Es la una.', en: 'It is one o\'clock.', note: 'ONE is singular: "es la una".' },
        { es: 'Son las tres.', en: 'It is three.', note: 'EVERYTHING ELSE is plural: "son las...".' },
        { es: 'Son las dos y media', en: 'It\'s 2:30 (and a half)' },
        { es: 'Son las cuatro y cuarto', en: 'It\'s 4:15 (and a quarter)' },
        { es: 'Son las cinco menos cuarto', en: 'It\'s 4:45 (5 minus a quarter)' },
      ] },
      { kind: 'h', text: 'Days of the week' },
      { kind: 'examples', items: [
        { es: 'lunes, martes, miércoles, jueves, viernes, sábado, domingo', en: 'Mon, Tue, Wed, Thu, Fri, Sat, Sun', note: 'Spanish weeks start on Monday. Days are NOT capitalized.' },
      ] },
      { kind: 'tip', text: 'To say "on Monday", use "el lunes". For "every Monday", use "los lunes". No preposition needed!' },
    ],
    practice: ['cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez', 'once', 'doce', 'veinte', 'cien', 'lunes', 'martes', 'miércoles', 'hora', 'minuto'],
  },
  {
    id: 'travel-phrases',
    level: 9,
    title: 'Travel Survival Phrases',
    emoji: '🧳',
    duration: '8 min',
    subtitle: 'Order food, book a room, hail a taxi, ask for help.',
    body: [
      { kind: 'h', text: 'At the restaurant' },
      { kind: 'examples', items: [
        { es: 'Una mesa para dos, por favor.', en: 'A table for two, please.' },
        { es: 'La carta, por favor.', en: 'The menu, please.' },
        { es: 'Quiero (un café / una ensalada).', en: 'I want (a coffee / a salad).' },
        { es: '¿Está incluida la propina?', en: 'Is the tip included?' },
        { es: 'La cuenta, por favor.', en: 'The bill, please.' },
      ] },
      { kind: 'h', text: 'At the hotel' },
      { kind: 'examples', items: [
        { es: 'Tengo una reserva.', en: 'I have a reservation.' },
        { es: '¿A qué hora es el desayuno?', en: 'What time is breakfast?' },
        { es: '¿Hay wifi en la habitación?', en: 'Is there wifi in the room?' },
        { es: 'Necesito una habitación para dos noches.', en: 'I need a room for two nights.' },
      ] },
      { kind: 'h', text: 'Getting around' },
      { kind: 'examples', items: [
        { es: '¿Dónde está la estación?', en: 'Where is the station?' },
        { es: 'Un billete a Madrid, por favor.', en: 'A ticket to Madrid, please.' },
        { es: '¿Cuánto tarda?', en: 'How long does it take?' },
        { es: 'Llévame al aeropuerto.', en: 'Take me to the airport.' },
      ] },
      { kind: 'h', text: 'Emergencies' },
      { kind: 'examples', items: [
        { es: '¡Ayuda!', en: 'Help!' },
        { es: 'Llame a la policía.', en: 'Call the police.' },
        { es: 'Me robaron.', en: 'I was robbed.' },
        { es: 'No me siento bien.', en: "I don't feel well." },
        { es: 'Necesito un médico.', en: 'I need a doctor.' },
      ] },
      { kind: 'tip', text: 'When in doubt, smile and start any request with "Hola, perdone…" — it instantly softens the question and signals you\'re trying.' },
    ],
    practice: ['restaurante', 'hotel', 'aeropuerto', 'billete', 'reserva', 'taxi', 'tren', 'autobús', 'mapa', 'equipaje', 'ayudar'],
  },
  {
    id: 'preterite',
    level: 10,
    title: 'Past Tense — Preterite',
    emoji: '⏪',
    duration: '10 min',
    subtitle: 'Talk about completed actions in the past — yesterday, last week, last summer.',
    body: [
      { kind: 'p', text: 'The PRETERITE is for actions that started AND ended in the past. "I ate", "she went", "we travelled". For ongoing or repeated past actions, you\'ll later need the imperfect — but preterite is your most useful past-tense tool by a mile.' },
      { kind: 'h', text: 'Regular -AR verbs (hablar)' },
      { kind: 'examples', items: [
        { es: 'yo hablé', en: 'I spoke' },
        { es: 'tú hablaste', en: 'you spoke' },
        { es: 'él / ella habló', en: 'he / she spoke' },
        { es: 'nosotros hablamos', en: 'we spoke', note: 'Same form as present!' },
        { es: 'ellos hablaron', en: 'they spoke' },
      ] },
      { kind: 'h', text: 'Regular -ER / -IR verbs (comer / vivir)' },
      { kind: 'examples', items: [
        { es: 'yo comí, viví', en: 'I ate, I lived' },
        { es: 'tú comiste, viviste', en: 'you ate, you lived' },
        { es: 'él / ella comió, vivió', en: 'he / she ate, lived' },
        { es: 'nosotros comimos, vivimos', en: 'we ate, we lived' },
        { es: 'ellos comieron, vivieron', en: 'they ate, they lived' },
      ] },
      { kind: 'tip', text: '-ER and -IR verbs share THE SAME preterite endings. Two for the price of one.' },
      { kind: 'h', text: 'The high-frequency irregulars (memorize these)' },
      { kind: 'examples', items: [
        { es: 'fui, fuiste, fue, fuimos, fueron', en: 'I went / was, etc. (ir & ser share the same preterite)' },
        { es: 'tuve, tuviste, tuvo, tuvimos, tuvieron', en: 'I had, etc. (tener)' },
        { es: 'estuve, estuviste, estuvo, estuvimos, estuvieron', en: 'I was [located], etc. (estar)' },
        { es: 'hice, hiciste, hizo, hicimos, hicieron', en: 'I did / made (hacer)', note: 'Note the spelling change: hizo, not "hico".' },
        { es: 'dije, dijiste, dijo, dijimos, dijeron', en: 'I said (decir)' },
      ] },
      { kind: 'tip', text: 'Notice: irregular preterite verbs DO NOT have accent marks on the yo and él/ella forms (tuve, hizo) — unlike the regular ones (hablé, habló). This is your visual cue to recognize them.' },
    ],
    practice: ['hablé', 'hablaste', 'habló', 'comí', 'comió', 'viví', 'fui', 'fue', 'tuve', 'tuvo', 'hice', 'hizo', 'dije', 'dijo'],
  },
  {
    id: 'connectors',
    level: 11,
    title: 'Connectors & Sounding Fluent',
    emoji: '🔗',
    duration: '6 min',
    subtitle: 'String thoughts together. This is the layer that separates "tourist" from "speaker".',
    body: [
      { kind: 'p', text: 'You can already conjugate verbs and form sentences. The next leap in fluency is connecting them — like the difference between "I went. It was good." and "I went, and even though I was tired, it was actually really good."' },
      { kind: 'h', text: 'The everyday glue' },
      { kind: 'examples', items: [
        { es: 'y', en: 'and' },
        { es: 'pero', en: 'but' },
        { es: 'porque', en: 'because' },
        { es: 'también', en: 'also / too' },
        { es: 'o', en: 'or' },
        { es: 'si', en: 'if' },
      ] },
      { kind: 'h', text: 'Sequencing' },
      { kind: 'examples', items: [
        { es: 'primero, después, luego, finalmente', en: 'first, after, then, finally' },
        { es: 'antes de comer', en: 'before eating' },
        { es: 'después de la cena', en: 'after dinner' },
      ] },
      { kind: 'h', text: 'Filler words that buy you time' },
      { kind: 'examples', items: [
        { es: 'pues…', en: 'well…' },
        { es: 'a ver…', en: 'let\'s see…' },
        { es: 'bueno…', en: 'OK / well…' },
        { es: 'es decir…', en: 'that is to say…' },
        { es: 'o sea…', en: 'I mean… / in other words…' },
      ] },
      { kind: 'tip', text: 'Native speakers use these constantly. Drop "pues" or "bueno" before answering, and your Spanish instantly sounds 30% more native — buying you a beat to think.' },
    ],
    practice: ['y', 'pero', 'porque', 'también', 'o', 'si', 'muy', 'mucho', 'poco'],
  },
  {
    id: 'mastery-tips',
    level: 12,
    title: 'Tips for Real Mastery',
    emoji: '🏆',
    duration: '6 min',
    subtitle: 'Strategies that take you from "I studied Spanish" to "I speak Spanish".',
    body: [
      { kind: 'p', text: 'You\'ve reached the hard part: building actual fluency. The drills in this app train recall — these tips help you turn it into real-world speaking ability.' },
      { kind: 'h', text: '1. Daily streak > weekend marathons' },
      { kind: 'p', text: '15 minutes every day beats 2 hours on Sunday. Your brain consolidates language during sleep — daily exposure compounds. The streak counter on this app exists for exactly this reason.' },
      { kind: 'h', text: '2. Speak before you\'re ready' },
      { kind: 'p', text: 'Most learners wait until they "feel ready" to speak. They never feel ready. Start saying "hola, ¿cómo estás?" to a barista today, even badly. Errors are how the language locks in.' },
      { kind: 'h', text: '3. Input + output, not just input' },
      { kind: 'rules', items: [
        { label: 'Input', text: 'Spanish music (Rosalía, Bad Bunny, Jorge Drexler), podcasts (Coffee Break Spanish, News in Slow Spanish), Netflix with Spanish audio + Spanish subtitles.' },
        { label: 'Output', text: 'Talk to yourself out loud. Narrate what you\'re doing in Spanish. Find a tutor on iTalki for 30 mins / week — game-changing.' },
      ] },
      { kind: 'h', text: '4. Embrace the "false friends"' },
      { kind: 'examples', items: [
        { es: 'embarazada', en: 'pregnant', note: 'NOT "embarrassed".' },
        { es: 'éxito', en: 'success', note: 'NOT "exit".' },
        { es: 'sopa', en: 'soup', note: 'NOT "soap".' },
        { es: 'asistir a', en: 'to attend', note: 'NOT "to assist".' },
        { es: 'ropa', en: 'clothing', note: 'NOT "rope".' },
      ] },
      { kind: 'h', text: '5. The "1000 word" milestone' },
      { kind: 'p', text: 'Linguists agree that ~1000 high-frequency words cover roughly 80% of everyday speech. You already have 300 in this app — keep adding 5-10 a week and you\'ll cross the threshold in 4-6 months. That\'s when listening "clicks".' },
      { kind: 'h', text: '6. The single most useful sentence' },
      { kind: 'examples', items: [
        { es: '¿Cómo se dice ___ en español?', en: 'How do you say ___ in Spanish?' },
      ] },
      { kind: 'tip', text: 'Memorize that one sentence cold. It\'s your forever-tool: when you don\'t know a word, point at it and ask. Every native speaker is suddenly your tutor.' },
      { kind: 'p', text: '¡Buena suerte! You\'ve got the foundation. Now go talk to a real human. 🇪🇸' },
    ],
    practice: ['suerte', 'también', 'pero', 'porque'],
  },
  {
    id: 'imperfect',
    level: 13,
    title: 'Past Tense — Imperfect',
    emoji: '🌅',
    duration: '8 min',
    subtitle: 'Talk about what *used to* happen, what *was* happening, what life *was like*.',
    body: [
      { kind: 'p', text: 'Where the preterite is for completed past actions ("I ate a sandwich"), the IMPERFECT is for ongoing, repeated, or descriptive past actions ("I used to eat sandwiches every day", "I was eating when she called", "the sun was shining"). Spanish speakers slide between the two constantly — and choosing the right one is what makes your past-tense storytelling click.' },
      { kind: 'h', text: 'Pattern: hablar (-AR)' },
      { kind: 'examples', items: [
        { es: 'yo hablaba', en: 'I was speaking / used to speak' },
        { es: 'tú hablabas', en: 'you were speaking' },
        { es: 'él / ella hablaba', en: 'he / she was speaking' },
        { es: 'nosotros hablábamos', en: 'we were speaking' },
        { es: 'ellos hablaban', en: 'they were speaking' },
      ] },
      { kind: 'h', text: 'Pattern: comer / vivir (-ER and -IR share endings)' },
      { kind: 'examples', items: [
        { es: 'yo comía / vivía', en: 'I was eating / living' },
        { es: 'tú comías / vivías', en: 'you were eating / living' },
        { es: 'él comía / vivía', en: 'he was eating / living' },
        { es: 'nosotros comíamos / vivíamos', en: 'we were eating / living' },
        { es: 'ellos comían / vivían', en: 'they were eating / living' },
      ] },
      { kind: 'tip', text: 'Only THREE verbs are irregular in the imperfect: ser → era / eras / era / éramos / eran ; ir → iba / ibas / iba / íbamos / iban ; ver → veía / veías / veía / veíamos / veían. Memorize these once and you\'re done with imperfect irregularity forever.' },
      { kind: 'h', text: 'When to use imperfect vs preterite' },
      { kind: 'rules', items: [
        { label: 'IMPERFECT — Background', text: 'Hacía sol. (It was sunny.) — sets the scene' },
        { label: 'IMPERFECT — Habit', text: 'De niño, yo iba a la playa cada verano. (As a kid I went to the beach every summer.)' },
        { label: 'IMPERFECT — In progress', text: 'Yo comía cuando llegó. (I was eating when he arrived.)' },
        { label: 'PRETERITE — One-shot', text: 'Ayer comí pizza. (Yesterday I ate pizza.) — one event, done.' },
        { label: 'BOTH together', text: 'Llovía cuando salí. (It WAS raining [imperfect, ongoing] when I LEFT [preterite, completed].)' },
      ] },
      { kind: 'h', text: 'Triggers — words that flag imperfect' },
      { kind: 'examples', items: [
        { es: 'siempre, todos los días, generalmente, nunca, mientras', en: 'always, every day, generally, never, while', note: 'These adverbs almost always trigger imperfect.' },
      ] },
      { kind: 'tip', text: 'A useful test: if you can substitute "used to ___" or "was ___ing" in English, it\'s imperfect. If it\'s a one-time finished event with a clear timestamp, it\'s preterite.' },
    ],
    practice: ['siempre', 'nunca', 'antes', 'todavía', 'a veces'],
  },
  {
    id: 'future',
    level: 14,
    title: 'The Future Tense',
    emoji: '🚀',
    duration: '7 min',
    subtitle: 'Talk about tomorrow, next year, what you "will" do.',
    body: [
      { kind: 'p', text: 'Spanish has TWO ways to express the future. The easy way ("ir a + infinitive") is what locals use 80% of the time in casual speech. The "real" future tense feels more formal, more written, more confident. Master both.' },
      { kind: 'h', text: 'The easy way: ir a + infinitive' },
      { kind: 'p', text: 'Same shape as English "going to ___". Conjugate IR in the present, then drop in any infinitive.' },
      { kind: 'examples', items: [
        { es: 'Voy a comer.', en: "I'm going to eat." },
        { es: 'Vamos a viajar a México.', en: "We're going to travel to Mexico." },
        { es: 'Va a llover.', en: "It's going to rain." },
        { es: '¿Qué vas a hacer mañana?', en: "What are you going to do tomorrow?" },
      ] },
      { kind: 'tip', text: 'Use this form for anything happening soon (today, tomorrow, this weekend) — it\'s the most natural option in everyday conversation.' },
      { kind: 'h', text: 'The real future tense — one set of endings for ALL verbs' },
      { kind: 'p', text: 'Take the WHOLE infinitive (don\'t drop -ar/-er/-ir!) and add: -é, -ás, -á, -emos, -án.' },
      { kind: 'examples', items: [
        { es: 'yo hablaré, comeré, viviré', en: 'I will speak, eat, live' },
        { es: 'tú hablarás, comerás, vivirás', en: 'you will speak, eat, live' },
        { es: 'él / ella hablará, comerá, vivirá', en: 'he / she will speak, eat, live' },
        { es: 'nosotros hablaremos, comeremos, viviremos', en: 'we will speak, eat, live' },
        { es: 'ellos hablarán, comerán, vivirán', en: 'they will speak, eat, live' },
      ] },
      { kind: 'h', text: 'The 12 irregular future stems (memorize)' },
      { kind: 'rules', items: [
        { label: 'tener → tendr-', text: 'tendré, tendrás, tendrá…' },
        { label: 'poder → podr-', text: 'podré (I will be able)' },
        { label: 'hacer → har-', text: 'haré (I will do)' },
        { label: 'decir → dir-', text: 'diré (I will say)' },
        { label: 'querer → querr-', text: 'querré (I will want)' },
        { label: 'saber → sabr-', text: 'sabré (I will know)' },
        { label: 'salir → saldr-', text: 'saldré (I will leave)' },
        { label: 'venir → vendr-', text: 'vendré (I will come)' },
        { label: 'poner → pondr-', text: 'pondré (I will put)' },
        { label: 'haber → habr-', text: 'habrá (there will be)' },
      ] },
      { kind: 'tip', text: 'A bonus use of the future tense: GUESSING. "Será mi madre" (it must be my mother) when the phone rings. "¿Qué hora será?" (I wonder what time it is). Native speakers use this constantly.' },
      { kind: 'p', text: '¡Lo lograste! You\'ve crossed all 14 levels. Now go talk to a real Spanish speaker — your foundation is solid. 🇪🇸✨' },
    ],
    practice: ['mañana', 'pronto', 'después', 'siempre'],
  },
];

// Theme rotation for the daily themed challenge (0=Sun..6=Sat)
export const DAILY_THEMES = [
  { day: 0, group: 'Family', label: 'Family Sunday', emoji: '👨‍👩‍👧' },
  { day: 1, group: 'Travel', label: 'Travel Monday', emoji: '🧳' },
  { day: 2, group: 'Food & Drink', label: 'Food Tuesday', emoji: '🍽️' },
  { day: 3, group: 'Places', label: 'Places Wednesday', emoji: '🏛️' },
  { day: 4, group: 'Body', label: 'Body Thursday', emoji: '👤' },
  { day: 5, group: 'Time', label: 'Time Friday', emoji: '⏰' },
  { day: 6, group: 'Adjectives', label: 'Adjective Saturday', emoji: '✨' },
];
